/**
 * Public Digest Landing Page — /digest/:slug OR /digest/@:username/:weekSlug
 * - Hero with audio player
 * - 3 trending topics
 * - 1 Ready-to-Forge brief
 * - Weekly stats
 * - Share buttons (LinkedIn, X, copy link) with per-platform tracking
 * - OG + Twitter card + JSON-LD structured data
 * - "Powered by iSupplyAI" + sign-up CTA
 * - Report button
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Linkedin, Twitter, Link2, CheckCircle2, Sparkles, Volume2, Flag } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicDigestPage = ({ routeUsername, routeWeekSlug, routeLegacySlug } = {}) => {
  const params = useParams();
  const username = routeUsername || params.username;
  const weekSlug = routeWeekSlug || params.weekSlug;
  const legacySlug = routeLegacySlug || params.slug;

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reported, setReported] = useState(false);

  const isCanonical = !!(username && weekSlug);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = isCanonical
          ? `${API_URL}/api/public/digest/by/${username}/${weekSlug}`
          : `${API_URL}/api/public/digest/${legacySlug}`;
        const { data } = await axios.get(url);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e.response?.status === 404 ? 'This digest does not exist or has been removed.' : 'Could not load digest.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isCanonical, username, weekSlug, legacySlug]);

  // Inject OG + Twitter Card + JSON-LD meta tags once data arrives
  useEffect(() => {
    if (!data) return;
    const industryTitle = ((data.industry || 'marketing').charAt(0).toUpperCase() + (data.industry || 'marketing').slice(1));
    const weekHuman = (() => {
      try {
        return new Date(data.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      } catch { return data.week_slug; }
    })();
    // Spec: "[Industry] Marketing Intelligence — Week of [Date] | iSupplyAI"
    const title = `${industryTitle} Intelligence — Week of ${weekHuman} | iSupplyAI`;
    const topTrend = data.content?.trends?.[0]?.topic || '';
    const description = topTrend
      ? `This week's top trending topics in ${industryTitle.toLowerCase()} that competitors haven't covered. AI-generated competitive intelligence digest by @${data.username || 'anonymous'}. Featured trend: ${topTrend}.`
      : `This week's top trending topics in ${industryTitle.toLowerCase()} that competitors haven't covered. AI-generated competitive intelligence digest by @${data.username || 'anonymous'}.`;
    document.title = title;

    const ogTitle = `🧠 ${industryTitle} Intelligence Digest — Week of ${weekHuman}`;
    const ogDescription = `3 trending topics your competitors missed + AI competitive analysis`;
    const ogImage = `${API_URL}/api/public/digest/${data.slug}/og.png`;
    const canonical = `${window.location.origin}/digest/@${data.username}/${data.week_slug}`;

    // Replace OR insert each tag — never duplicate (avoids 2x og:title from index.html defaults)
    const upsertMeta = (selector, attr, name, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      el.setAttribute('data-digest-injected', 'true');
      return el;
    };

    const tags = [
      { sel: 'meta[name="description"]', attr: 'name', name: 'description', content: description },
      { sel: 'meta[property="og:type"]', attr: 'property', name: 'og:type', content: 'article' },
      { sel: 'meta[property="og:title"]', attr: 'property', name: 'og:title', content: ogTitle },
      { sel: 'meta[property="og:description"]', attr: 'property', name: 'og:description', content: ogDescription },
      { sel: 'meta[property="og:image"]', attr: 'property', name: 'og:image', content: ogImage },
      { sel: 'meta[property="og:url"]', attr: 'property', name: 'og:url', content: canonical },
      { sel: 'meta[property="og:site_name"]', attr: 'property', name: 'og:site_name', content: 'iSupplyAI Pulse' },
      { sel: 'meta[name="twitter:card"]', attr: 'name', name: 'twitter:card', content: 'summary_large_image' },
      { sel: 'meta[name="twitter:title"]', attr: 'name', name: 'twitter:title', content: ogTitle },
      { sel: 'meta[name="twitter:description"]', attr: 'name', name: 'twitter:description', content: ogDescription },
      { sel: 'meta[name="twitter:image"]', attr: 'name', name: 'twitter:image', content: ogImage },
    ];
    const elements = tags.map(t => upsertMeta(t.sel, t.attr, t.name, t.content));

    // JSON-LD structured data — replace any prior digest-jsonld script
    const prev = document.getElementById('digest-jsonld');
    if (prev) prev.remove();
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.id = 'digest-jsonld';
    ld.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: ogTitle,
      description: ogDescription,
      image: ogImage,
      url: canonical,
      datePublished: data.published_at,
      author: { '@type': 'Person', name: data.user_name || data.username },
      publisher: { '@type': 'Organization', name: 'iSupplyAI' },
    });
    document.head.appendChild(ld);

    return () => {
      // Only remove tags we created (data-digest-injected='true'); leave originals from index.html alone
      elements.forEach(e => {
        if (e && e.getAttribute('data-digest-injected') === 'true') e.remove();
      });
      const cur = document.getElementById('digest-jsonld');
      if (cur) cur.remove();
    };
  }, [data]);

  const track = async (event_type, platform = null) => {
    if (!data?.slug) return;
    try {
      await axios.post(`${API_URL}/api/public/digest/${data.slug}/track`, {
        event_type,
        platform,
        referrer: document.referrer || null,
      });
    } catch { /* non-blocking */ }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = `${(data?.user_name || 'My').split(' ')[0]}'s weekly Intelligence Pulse — 3 trends competitors haven't touched, plus a 60-sec CEO briefing.`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      track('share', 'copy');
      setTimeout(() => setLinkCopied(false), 2500);
    } catch { /* noop */ }
  };

  const submitReport = async () => {
    if (!data?.slug) return;
    setReporting(true);
    try {
      await axios.post(`${API_URL}/api/public/digest/${data.slug}/report`, { reason: 'user_report' });
      setReported(true);
    } finally {
      setReporting(false);
    }
  };

  // Time-on-page tracking — single beacon at unmount or when tab hides.
  // Caps at 30 min server-side; we only fire if user spent ≥3s on the page.
  useEffect(() => {
    if (!data?.slug) return;
    const startedAt = Date.now();
    let fired = false;
    const fire = () => {
      if (fired) return;
      const elapsed = Date.now() - startedAt;
      if (elapsed < 3000) return;
      fired = true;
      const url = `${API_URL}/api/public/digest/${data.slug}/track`;
      const payload = JSON.stringify({ event_type: 'time_on_page', time_on_page_ms: elapsed });
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
        } else {
          fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {});
        }
      } catch { /* noop */ }
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') fire(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', fire);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', fire);
      fire();
    };
  }, [data?.slug]);

  // Stash the digest slug for signup-attribution. When the visitor signs up
  // (App.js reads localStorage during the signup handler), the signup event
  // gets fired against this slug so creators see signups they drove.
  useEffect(() => {
    if (!data?.slug) return;
    try {
      localStorage.setItem('isupplyai_digest_attribution', JSON.stringify({
        slug: data.slug,
        username: data.username,
        ts: Date.now(),
      }));
    } catch { /* noop */ }
  }, [data?.slug, data?.username]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center text-gray-400" data-testid="public-digest-loading">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading digest…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#020617] flex flex-col items-center justify-center text-center px-4" data-testid="public-digest-error">
        <div className="text-5xl mb-4">🔍</div>
        <h1 className="text-2xl font-bold text-white mb-2">Digest not found</h1>
        <p className="text-gray-400 mb-6 max-w-md">{error || 'This digest may have been unpublished.'}</p>
        <Link to="/digest/featured" className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold text-sm">
          Browse the directory →
        </Link>
      </div>
    );
  }

  const content = data.content || {};
  const trends = content.trends || [];
  const brief = content.ready_to_forge_brief;
  const stats = content.stats || {};
  const audioSrc = `${API_URL}/api/public/digest/${data.slug}/audio`;
  const firstName = (data.user_name || 'There').split(' ')[0];
  const archiveUrl = `/digest/@${data.username}`;
  const isEnterprise = (data.creator_tier || '').toLowerCase() === 'enterprise';
  const industryTitle = ((data.industry || 'marketing').charAt(0).toUpperCase() + (data.industry || 'marketing').slice(1));
  const weekHuman = (() => {
    try { return new Date(data.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); }
    catch { return data.week_slug; }
  })();

  return (
    <div className="min-h-screen bg-[#020617] text-white" data-testid="public-digest-page">
      {/* Top branding bar */}
      <div className="border-b border-gray-800 bg-gray-900/60 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white font-bold text-sm">
            <span>🧠</span>
            <span>iSupplyAI <span className="text-purple-400 font-normal">Pulse</span></span>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/?auth=login"
              className="text-xs px-3 py-1.5 rounded-lg text-gray-300 hover:text-white border border-gray-700 hover:border-gray-500"
              data-testid="public-digest-login"
            >
              Login
            </Link>
            <Link
              to="/"
              onClick={() => track('cta_click')}
              className="text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold"
              data-testid="public-digest-cta-top"
            >
              Sign up →
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-10 space-y-6">

        {/* Hero */}
        <div className="rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-900/40 via-gray-900/80 to-blue-900/30 p-6 sm:p-8">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase tracking-[0.25em] text-purple-300 font-bold">🧠 Intelligence Pulse</div>
              <h1 className="text-3xl sm:text-4xl font-black text-white leading-tight mt-1" data-testid="public-digest-h1">
                {firstName}'s {industryTitle} Intelligence Digest
              </h1>
              <div className="text-sm sm:text-base text-purple-200 mt-1 font-semibold" data-testid="public-digest-week-line">
                Week of {weekHuman}
              </div>
              <Link to={archiveUrl} className="text-xs text-purple-400 hover:text-purple-300 mt-2 inline-block" data-testid="public-digest-archive-link">
                @{data.username} · View all digests →
              </Link>
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              <span className="px-2.5 py-1 rounded-full bg-gray-800 text-gray-300 text-[10px] font-bold uppercase tracking-widest border border-gray-700" data-testid="public-digest-industry">
                {data.industry || 'marketing'}
              </span>
              {isEnterprise && (
                <span className="px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-300 text-[10px] font-bold uppercase tracking-widest border border-amber-500/40" data-testid="public-digest-enterprise-badge" title="iSupplyAI Enterprise creator">
                  ⭐ Enterprise
                </span>
              )}
            </div>
          </div>
          <p className="text-gray-400 text-sm sm:text-base">
            A 60-second strategic briefing + 3 trends competitors haven't touched.
          </p>

          {/* Audio player */}
          {content.audio_size_kb > 0 && (
            <div className="mt-6 p-4 rounded-xl bg-black/40 border border-amber-500/20" data-testid="public-digest-audio-section">
              <div className="flex items-center gap-2 text-amber-300 text-xs font-bold uppercase tracking-widest mb-2">
                <Volume2 className="w-4 h-4" />
                <span>60-sec CEO briefing</span>
              </div>
              <audio controls preload="none" src={audioSrc} className="w-full" data-testid="public-digest-audio-player" />
              <a href={audioSrc} download={`iSupplyAI-Briefing-${data.slug}.mp3`} className="text-xs text-amber-400 hover:text-amber-300 mt-2 inline-block" data-testid="public-digest-audio-download">
                Download MP3 →
              </a>
            </div>
          )}
        </div>

        {/* Share row */}
        <div className="rounded-xl border border-gray-800 bg-gray-900/40 p-4 flex flex-wrap items-center gap-2" data-testid="public-digest-share-row">
          <span className="text-xs text-gray-400 mr-2">Share:</span>
          <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" onClick={() => track('share', 'linkedin')}
             className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0a66c2] hover:bg-[#0d77e0] text-white text-xs font-semibold" data-testid="public-digest-share-linkedin">
            <Linkedin className="w-3.5 h-3.5" /> LinkedIn
          </a>
          <a href={twitterUrl} target="_blank" rel="noopener noreferrer" onClick={() => track('share', 'twitter')}
             className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black hover:bg-gray-800 text-white text-xs font-semibold border border-gray-700" data-testid="public-digest-share-twitter">
            <Twitter className="w-3.5 h-3.5" /> X
          </a>
          <button onClick={copyLink}
             className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-white text-xs font-semibold border border-gray-700" data-testid="public-digest-share-copy">
            {linkCopied ? <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Copied!</> : <><Link2 className="w-3.5 h-3.5" /> Copy link</>}
          </button>
          <span className="ml-auto text-xs text-gray-500">{data.view_count || 0} views</span>
          {!reported ? (
            <button
              onClick={submitReport}
              disabled={reporting}
              className="text-[10px] text-gray-600 hover:text-red-400 flex items-center gap-1"
              title="Report this digest"
              data-testid="public-digest-report-btn"
            >
              <Flag className="w-3 h-3" /> {reporting ? 'Reporting…' : 'Report'}
            </button>
          ) : (
            <span className="text-[10px] text-emerald-500">Reported</span>
          )}
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-3 gap-3" data-testid="public-digest-stats">
          <div className="rounded-xl bg-gray-900/60 border border-gray-800 p-4 text-center">
            <div className="text-xs text-gray-400 uppercase tracking-widest">Trends</div>
            <div className="text-2xl font-bold text-purple-300 mt-1">{trends.length}</div>
          </div>
          <div className="rounded-xl bg-gray-900/60 border border-gray-800 p-4 text-center">
            <div className="text-xs text-gray-400 uppercase tracking-widest">Comp. articles</div>
            <div className="text-2xl font-bold text-amber-300 mt-1">{stats.competitor_articles_published || 0}</div>
          </div>
          <div className="rounded-xl bg-gray-900/60 border border-gray-800 p-4 text-center">
            <div className="text-xs text-gray-400 uppercase tracking-widest">Forged</div>
            <div className="text-2xl font-bold text-cyan-300 mt-1">{stats.forged_this_week || 0}</div>
          </div>
        </div>

        {/* Trends */}
        {trends.length > 0 ? (
          <div className="rounded-2xl border border-gray-800 bg-gray-900/50 overflow-hidden" data-testid="public-digest-trends">
            <div className="px-5 py-3 border-b border-gray-800 bg-gradient-to-r from-purple-500/10 to-transparent">
              <div className="text-xs uppercase tracking-widest text-purple-300 font-bold">🔥 Trends Worth Forging</div>
            </div>
            <ul className="divide-y divide-gray-800">
              {trends.map((t, i) => (
                <li key={i} className="px-5 py-4 flex gap-3" data-testid={`public-digest-trend-${i}`}>
                  <span className="text-lg font-bold text-purple-400 flex-shrink-0 w-7">#{i + 1}</span>
                  <div>
                    <div className="font-semibold text-white">{t.topic || 'Trending topic'}</div>
                    <div className="text-sm text-gray-400 mt-1">{t.why_trending || t.content_angle || 'Gaining momentum in this space.'}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-800 bg-gray-900/40 p-6 text-center" data-testid="public-digest-trends-empty">
            <div className="text-3xl mb-2">📡</div>
            <div className="text-sm font-semibold text-white mb-1">Trends scan running</div>
            <div className="text-xs text-gray-400">Fresh signals will populate here once {firstName}'s next intelligence sweep completes.</div>
          </div>
        )}

        {/* Ready-to-forge brief */}
        {brief && (
          <div className="rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-900/30 via-gray-900/80 to-orange-900/20 p-5 sm:p-6" data-testid="public-digest-brief">
            <div className="text-xs uppercase tracking-widest text-purple-300 font-bold mb-2">⚒️ Ready-to-Forge Brief</div>
            <h3 className="text-xl font-bold text-white leading-snug">{brief.suggested_title}</h3>
            <p className="text-sm text-gray-300 mt-3 leading-relaxed">{brief.why_it_matters}</p>
            {brief.target_keyword && (
              <div className="mt-3 text-sm text-gray-400">
                <span className="text-cyan-400 font-semibold">Target keyword: </span>
                {brief.target_keyword}
              </div>
            )}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-900/40 to-blue-900/30 p-6 sm:p-8 text-center" data-testid="public-digest-cta-bottom">
          <div className="flex items-center justify-center gap-2 text-purple-300 text-xs font-bold uppercase tracking-[0.25em] mb-2">
            <Sparkles className="w-3.5 h-3.5" /> Powered by iSupplyAI
          </div>
          <h2 className="text-2xl font-black text-white mb-2">Get your own weekly Intelligence Pulse</h2>
          <p className="text-sm text-gray-300 max-w-md mx-auto mb-5">
            Trends competitors haven't touched, AI-generated briefs, and a 60-second CEO audio briefing — every Monday at 9am your local time.
          </p>
          <Link
            to="/"
            onClick={() => track('cta_click')}
            className="inline-block px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold text-sm shadow-lg shadow-purple-500/30 hover:scale-105 transition-transform"
            data-testid="public-digest-signup-cta"
          >
            Start free →
          </Link>
        </div>

        <div className="text-center text-xs text-gray-600 pt-2">
          Published {new Date(data.published_at).toLocaleDateString()} · <code className="text-gray-500">@{data.username}/{data.week_slug}</code>
        </div>
      </div>
    </div>
  );
};

export default PublicDigestPage;
